<template>
  <div
    class="full-slide-asset-menu"
    :class="{ 'full-slide-asset-menu--options': showOptions }"
  >
    <div class="full-slide-asset-menu__toolbar">
      <div class="full-slide-asset-menu__toolbar-start">
        <div
          v-if="showOptions"
          class="full-slide-asset-menu__toolbar-item
            full-slide-asset-menu__toolbar-item--no-margin"
        >
          <NebulaTitleTooltip
            :position="tooltipsPosition"
            :text="$t('Close')"
          >
            <NebulaButton
              icon-left="arrow-left"
              type="flat"
              :aria-label="$t('Close Video Options')"
              @click="onCloseClick"
            />
          </NebulaTitleTooltip>
        </div>
        <div class="full-slide-asset-menu__toolbar-item">
          <span>{{ $t('Video') }}</span>
        </div>
      </div>
      <div class="full-slide-asset-menu__toolbar-end">
        <div
          v-if="!showVideoOptions"
          class="full-slide-asset-menu__toolbar-item"
        >
          <NebulaTitleTooltip
            :position="tooltipsPosition"
            :text="$t('Video Settings')"
          >
            <NebulaButton
              icon-left="video"
              type="flat"
              :aria-label="$t('Edit Video Settings')"
              @click="onEditClick"
            />
          </NebulaTitleTooltip>
        </div>
        <div
          v-if="showOptions"
          class="full-slide-asset-menu__toolbar-item"
        >
          <NebulaButton
            type="ghost"
            target="_blank"
            size="s"
            :link="viewInContentUrl"
            :text="$t('View in Atlas')"
          />
        </div>
        <div
          class="full-slide-asset-menu__toolbar-item"
        >
          <NebulaTitleTooltip
            :position="tooltipsPosition"
            :text="$t('Replace')"
          >
            <NebulaButton
              icon-left="refresh"
              type="flat"
              :aria-label="$t('Replace Video')"
              @click="onReplaceClick"
            />
          </NebulaTitleTooltip>
        </div>
      </div>
    </div>
    <div
      aria-live="polite"
      class="nebula-screenreader-only"
    >
      <div v-if="showOptions">
        {{ $t('Video options open') }}
      </div>
      <div v-else>
        {{ $t('Video options closed') }}
      </div>
    </div>
    <div
      v-if="showOptions"
      class="full-slide-asset-menu__options"
    >
      <VideoOptions
        v-if="showVideoOptions"
        is-full-slide-asset
        :page="page"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep, first } from 'lodash-es';
import { mapActions } from 'pinia';
import { NebulaButton, NebulaTitleTooltip } from '@discoveryedu/nebula-components';
import VideoOptions from '@/components/VideoOptions.vue';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
  useModalStore,
} from '@/stores';

export default {
  components: {
    NebulaButton,
    NebulaTitleTooltip,
    VideoOptions,
  },
  data() {
    return {
      showVideoOptions: false,
    };
  },
  props: {
    page: {
      type: Object,
      default: null,
    },
    asset: {
      type: Object,
      default: null,
    },
    viewInContentUrl: {
      type: String,
      default: null,
    },
    tooltipsPosition: {
      type: String,
      default: 'block-start',
    },
  },
  computed: {
    showOptions() {
      return this.showVideoOptions;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_PAGE,
    ]),
    ...mapActions(useModalStore, [
      types.CLEAR_MODALS,
      types.OPEN_MODAL,
    ]),
    onEditClick() {
      this.showVideoOptions = true;
    },
    onCloseClick() {
      this.showVideoOptions = false;
    },
    onReplaceClick() {
      this[types.OPEN_MODAL]({
        type: 'SearchAssets',
        onConfirm: this.replaceAsset,
        singleSelect: true,
      });
    },
    replaceAsset(assets) {
      const asset = first(assets);
      if (asset) {
        const page = cloneDeep(this.page);
        page.options.asset_id = asset.id;
        page.title = asset.title;
        // Clear video options if asset is not a video
        if (asset.group !== 'video') {
          page.options.auto_advance = false;
          page.options.auto_play = false;
          page.options.loop_video = false;
        }
        this[types.UPDATE_PAGE](page);
        this[types.CLEAR_MODALS]();
      }
    },
  },
};
</script>

<style lang="stylus">
.full-slide-asset-menu {
  background: $nebula-color-white;
  border-radius: $nebula-border-radius-small;
  padding: $nebula-space-half;

  &--options {
    min-width: 350px;
    padding: $nebula-space-1x;
  }

  &__toolbar {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  &__options {
    border-top: 1px solid $nebula-color-platform-interface-300;
    margin-top: $nebula-space-1x;
    padding: $nebula-space-1x;
  }

  &__toolbar-start {
    align-items: center;
    display: flex;
    font-size: $nebula-font-size-body-2;
    font-weight: bold;
    justify-content: center;
  }

  &__toolbar-end {
    align-items: center;
    display: flex;
    margin-inline-start: $nebula-space-1x;
  }

  &__toolbar-item {
    margin-inline-start: $nebula-space-1x;

    &--no-margin {
      margin: 0;
    }
  }

  .nebula-button {
    svg {
      fill: $nebula-color-black;
    }
  }

  button.nebula-button {
    &:hover, &:active, &:focus {
      background: $comet-color-neutral-90; // match other menu buttons for now.
    }
  }

  .nebula-title-tooltip {
    z-index: 2; // on top of nebula checkboxes.
  }
}
</style>
